import React, { useState } from 'react';
import './InhibitRestartButton.less';
import { Telematics } from 'types/IEquipmentEntry';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';
import RestartInhibitDefaultIcon from 'assets/images/restart_inhibit_default.svg';
import { Switch } from '@progress/kendo-react-inputs';
import { InhibitRestart } from 'app/pages/InhibitRestartPage';
import { useNotificationsContext } from 'context/useNotificationsContext';
import { SubscriptionInfo } from 'app/hooks/subscriptions/useSubscriptionInfo';
import useToggleLogic from 'app/pages/Equipment/EquipmentContent/components/InhibitRestartButton/useToggleLogic';
import { InhibitRestartStatus } from 'app/pages/InhibitRestartPage/type';
import PremiumFeatureSnackbar from 'app/components/subscriptions/snackbars/PremiumFeatureSnackbar';

interface Props {
  equipmentId: string;
  telematics?: Telematics;
  hasAccess: boolean;
  subscriptionInfo?: SubscriptionInfo;
  onUpgrade: () => void;
}

export default function InhibitRestartButton({
  equipmentId,
  telematics,
  hasAccess,
  subscriptionInfo,
  onUpgrade,
}: Props) {
  const { t } = useTranslation();
  const isSubscriptionsFlagEnabled = useFlag(FeatureName.SUBSCRIPTIONS);
  const { addSnackbar, removeSnackbar } = useNotificationsContext();

  const [
    isInhibitRestartDialogVisibility,
    setIsInhibitRestartDialogVisibility,
  ] = useState(false);

  const { canModify, commandStatus, equipmentFeatureStatus, equipmentStatus } =
    telematics?.restartInhibitStatus ?? {};

  const hasPremiumSubscription = subscriptionInfo?.hasPremium ?? false;
  const isBasicSubscription =
    isSubscriptionsFlagEnabled && !hasPremiumSubscription;

  const isUnlockFeatureTextVisible =
    isBasicSubscription &&
    (equipmentFeatureStatus === InhibitRestartStatus.Disabled ||
      (equipmentFeatureStatus === InhibitRestartStatus.Enabled &&
        equipmentStatus === InhibitRestartStatus.RestartEnabled &&
        commandStatus === InhibitRestartStatus.RestartEnabled));

  const { isChecked, isDisabled, notificationType } = useToggleLogic({
    hasAccess,
    canModify,
    isSubscriptionsFlagEnabled,
    hasPremiumSubscription,
    equipmentFeatureStatus,
    equipmentStatus,
    commandStatus,
  });

  function handleSwitchChange() {
    switch (notificationType) {
      case 'feature-available-in-premium':
        addSnackbar(
          <PremiumFeatureSnackbar
            onClick={() => {
              removeSnackbar();
              onUpgrade();
            }}
          />,
          'screen-centered',
          5000,
        );
        break;
      case 'you-do-not-have-access':
        addSnackbar(
          <div className="snackbar-container">
            <span>
              {t('subscriptions.notifications.you_do_not_have_access')}
            </span>
          </div>,
        );
        break;
      default:
        setIsInhibitRestartDialogVisibility(true);
        break;
    }
  }

  return (
    <>
      {isInhibitRestartDialogVisibility && (
        <InhibitRestart
          onClose={() => {
            setIsInhibitRestartDialogVisibility(false);
          }}
        />
      )}

      <div
        className={clsx('inhibit-restart-button', {
          'inhibit-restart-button_premium': hasPremiumSubscription,
        })}
      >
        <img
          src={RestartInhibitDefaultIcon}
          alt="Inhibit Restart"
          className="inhibit-restart-button__icon"
        />

        <div className="inhibit-restart-button__text-block ">
          <span className="inhibit-restart-button__title">
            {t('kubota_now_inhibit_restart')}
          </span>

          {isUnlockFeatureTextVisible && (
            <p className="inhibit-restart-button__basic-sub-subtitle">
              <Trans i18nKey="unlock_this_feature_with" />
            </p>
          )}
        </div>

        <Switch
          className={clsx('inhibit-restart-button__switch', {
            'inhibit-restart-button__switch_disabled': isDisabled,
          })}
          checked={isChecked}
          onLabel=""
          offLabel=""
          onChange={handleSwitchChange}
        />
      </div>
    </>
  );
}
